import usePrivateAxios from "@/common/api/usePrivateAxios"
import { getRailsUpdate } from "@/common/utils/getRailsUpdate"
import { transformNewProducts } from "@/common/utils/productTransformers"
import { mapToDeletion } from "@/common/utils/railsHelpers"
import { unFormatPercentage } from "@/common/utils/unFormatters"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { useMutation } from "@tanstack/react-query"

interface UseUpdateEstablishmentLeadProps {
  id?: string
  establishmentLead: EstablishmentLeadData
  file?: Blob
  onSuccess?: () => void
  onError?: (error: any) => void
}

export const useUpdateEstablishmentLead = ({
  id,
  establishmentLead,
  file,
  onSuccess,
  onError,
}: UseUpdateEstablishmentLeadProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['updateEstablishmentLead'],
    mutationFn: (data: EstablishmentLeadsFunnelUpdateValidationSchema) => {
      const lead = {
        commercial_manager_id: data.commercial_manager?.value,
        commercial_manager_substitute_id: data.commercial_manager_substitute?.value || 'null',
        accreditation_information_attributes: {
          accreditation_information_product_categories_attributes: getRailsUpdate(
            establishmentLead?.accreditation_information.accreditation_information_product_categories || [],
            data?.accreditation_information?.product_categories || [],
            (x) => x.product_category.id,
            (x) => x.value,
            mapToDeletion,
            transformNewProducts
          ),
          accreditation_information_capture_methods_attributes: getRailsUpdate(
            establishmentLead?.accreditation_information.accreditation_information_capture_methods || [],
            data?.accreditation_information?.capture_methods || [],
            (x) => x.capture_method.id,
            (x) => x.value,
            mapToDeletion,
            (x) => ({ capture_method_id: x.value })
          ),
          photos_attachments_attributes: getRailsUpdate(
            establishmentLead.accreditation_information.photos || [],
            data.accreditation_information?.photos || [],
            (photo) => photo.attachment_id,
            (image) => image.id,
            (image) => ({ id: image.attachment_id, _destroy: true }),
            (image) => ({ blob_id: image.id })
          ),
          accreditation_information_pos_machines_attributes: getRailsUpdate(
            establishmentLead?.accreditation_information.accreditation_information_pos_machines || [],
            data?.accreditation_information?.accreditation_information_pos_machines?.machine_id || [],
            (x) => x.machine.id,
            (x) => x.value,
            mapToDeletion,
            (x) => ({ machine_id: x.value })
          ),
          payment_advance_daily_interest: data.accreditation_information?.payment_advance_daily_interest
            ? unFormatPercentage(data.accreditation_information?.payment_advance_daily_interest)
            : undefined,
        },
        general_information_attributes: {
          ...data.general_information,
          commercial_kind: data.general_information?.commercial_kind?.value,
          flag_id: data.general_information?.flag?.value,
          simple_national_optant: !!data.general_information?.simple_national_optant?.value
            ? JSON.parse(data.general_information?.simple_national_optant?.value)
            : undefined,
          company_logo: file ? file : undefined,
          phone_attributes: {
            ...data.general_information?.phone
          }
        },
        address_attributes: {
          ...data.address_attributes,
        },
        bank_account_information_attributes: {
          ...data.bank_account_information,
          bank_institution_id: data.bank_account_information?.bank_institution?.value,
        },
        observations: data.observations,
        ca_module_enabled: data.ca_module_enabled,
        cm_module_enabled: data.cm_module_enabled,
        tow_service_available: data.tow_service_available,
      }

      return privateAxios.put(`/funnel/establishment/leads/${id}`, {
        lead
      })
    },
    onSuccess,
    onError,
  })
}
